import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Mail, MessageCircle, Clock, Globe, UserCog, CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

function initialsOf(name = "") {
  return name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm font-medium text-foreground">{value || "—"}</div>
      </div>
    </div>
  );
}

export default function UserProfile() {
  const { id } = useParams();
  const [m, setM] = useState(null);

  useEffect(() => {
    apiFetch(`/members/${id}`).then(setM).catch((e) => toast.error(e.message));
  }, [id]);

  const savePref = async (patch) => {
    try { await apiFetch(`/members/${id}`, { method: "PUT", body: JSON.stringify(patch) }); toast.success("Preference updated"); }
    catch (e) { toast.error(e.message); }
  };

  if (!m) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="animate-fade-up">
      <Button asChild variant="ghost" size="sm" className="mb-3 gap-2 text-muted-foreground">
        <Link to="/team/members" data-testid="back-to-members"><ArrowLeft className="h-4 w-4" /> All members</Link>
      </Button>
      <PageHeader eyebrow="Member Profile" title={m.full_name || m.login_email}
        actions={<Button variant="outline" data-testid="edit-profile">Edit Profile</Button>} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 text-center shadow-soft lg:col-span-1" data-testid="profile-summary">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-secondary text-2xl font-semibold text-primary">{initialsOf(m.full_name)}</span>
          <h2 className="mt-4 font-heading text-2xl font-semibold text-foreground">{m.full_name || "—"}</h2>
          <p className="text-sm text-muted-foreground">{m.role || "—"}</p>
          <div className="mt-4 flex justify-center"><StatusBadge status={m.status} /></div>
        </Card>

        <div className="space-y-6 lg:col-span-2">
          <Card className="p-6 shadow-soft" data-testid="profile-details">
            <h3 className="mb-2 font-heading text-lg font-semibold">Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-8">
              <Field icon={Mail} label="Login email" value={m.login_email} />
              <Field icon={MessageCircle} label="Preferred channel" value={m.preferred_channel} />
              <Field icon={UserCog} label="Notification email" value={m.notification_email} />
              <Field icon={Globe} label="Time zone" value={m.timezone} />
              <Field icon={Clock} label="Last login" value={m.last_login ? new Date(m.last_login).toLocaleString() : "Never"} />
              <Field icon={CalendarDays} label="Date joined" value={m.date_joined ? new Date(m.date_joined).toLocaleDateString() : "—"} />
            </div>
          </Card>

          <Card className="p-6 shadow-soft" data-testid="profile-preferences">
            <h3 className="mb-3 font-heading text-lg font-semibold">Notification preferences</h3>
            <div className="space-y-1">
              {[
                { label: "Email reminders", key: "email_reminders" },
                { label: "WhatsApp reminders", key: "whatsapp_reminders" },
                { label: "Daily report required", key: "daily_report_required" },
              ].map((p) => (
                <div key={p.key} className="flex items-center justify-between border-b border-border py-3 last:border-0">
                  <span className="text-sm text-foreground">{p.label}</span>
                  <Switch defaultChecked={!!m[p.key]} onCheckedChange={(v) => savePref({ [p.key]: v })}
                    data-testid={`pref-${p.label.toLowerCase().replace(/\s+/g, "-")}`} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
